import { Button, Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import * as yup from 'yup';
import { Selection } from 'src/modules/groupMessageEmail/models/selection';
import { yupResolver } from '@hookform/resolvers/yup';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { deleteSelection, getSelectionById, updateSelection } from 'src/shared/services/selectionService';
import { toast } from 'react-toastify';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { SelectionPutRequest } from '../models/selectionPutRequest';

type SelectionDetailProps = {
  selectionId: number;
  setSelectionDetailModal: (state: { open: boolean; selectionId: number | undefined }) => void;
  setRefetchSelectionsDataGrid: (refetchSelectionsDataGrid: boolean) => void;
};

export const SelectionDetails = (props: SelectionDetailProps) => {
  const { selectionId, setSelectionDetailModal, setRefetchSelectionsDataGrid } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const [selection, setSelection] = useState<Selection>();
  const [loading, setLoading] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);

  const minimumCharRequired = 5;
  const maximumCharRequired = 50;
  const minCharRequiredMessage = t('common:validationMessages.minLength', { count: minimumCharRequired });
  const maxCharRequiredMessage = t('common:validationMessages.maxLength', { count: maximumCharRequired });

  const schema = yup
    .object({
      selection_id: yup.number().required(),
      selectionName: yup.string().min(5, minCharRequiredMessage).max(50, maxCharRequiredMessage).required(),
      selectoinInfo: yup.string().optional().nullable(),
    })
    .required();

  const formMethods = useForm<SelectionPutRequest>({
    resolver: yupResolver(schema),
    defaultValues: {
      selection_id: selectionId,
      selectionName: '',
      selectionInfo: '',
      json: {},
    },
  });
  const { handleSubmit } = formMethods;

  useEffect(() => {
    setLoading(true);
    const fetchSelection = async () => {
      try {
        const res = await getSelectionById(selectionId);
        if (isMounted()) {
          setSelection(res.data);
          formMethods.reset({
            selection_id: res.data.selection_id,
            selectionName: res.data.selectionName,
            selectionInfo: res.data.selectionInfo ?? '',
            json: res.data.json,
          });
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.error(e);
      } finally {
        if (isMounted()) setLoading(false);
      }
    };
    fetchSelection();
  }, [formMethods, isMounted, selectionId]);

  const onHandleDelete = async () => {
    setLoading(true);
    try {
      await deleteSelection(selectionId);
      if (isMounted()) {
        toast.success(<>{t('member:selection.deletedSelectionSuccess')}</>);
        setSelectionDetailModal({ open: false, selectionId: undefined });
        setRefetchSelectionsDataGrid(true);
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function removeEmptyArraysAndNulls(obj: Record<string, any>) {
    if (!obj) return {};
    return Object.fromEntries(
      Object.entries(obj).filter(([_, value]) => value !== undefined && value !== null && !(Array.isArray(value) && value.length === 0)),
    );
  }

  const onSubmit = async (data: SelectionPutRequest) => {
    setLoading(true);

    const jsonObj = typeof data.json === 'string' ? JSON.parse(data.json) : data.json;
    const filterFormValues = removeEmptyArraysAndNulls(jsonObj ?? {});
    const dataToPost: SelectionPutRequest = {
      selection_id: selectionId,
      selectionName: data.selectionName,
      selectionInfo: data.selectionInfo,
      json: JSON.stringify(filterFormValues),
    };

    try {
      await updateSelection(dataToPost);
      if (isMounted()) {
        toast.success(<>{t('member:selection.updatedSelectionSuccess')}</>);
        setRefetchSelectionsDataGrid(true);
        setSelectionDetailModal({ open: false, selectionId: undefined });
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  return (
    <>
      <FormProvider {...formMethods}>
        <form id='update-selection-form' onSubmit={handleSubmit(onSubmit)}>
          <GenericDialog
            open
            maxWidth='sm'
            onSave={handleSubmit(onSubmit)}
            onClose={() => setSelectionDetailModal({ open: false, selectionId: undefined })}
            dialogTitle='Urval'
            dialogContent={
              <>
                {selection ? (
                  <GridWrapper>
                    <Grid item xs={12} sx={{ pb: { xs: 2 } }}>
                      <ControlledTextField name='selectionName' label={t('member:memberAdvancedSearch.name')} required autoFocus />
                    </Grid>
                    <Grid item xs={12} sx={{ pb: { xs: 2 } }}>
                      <ControlledTextField multiline minRows={5} name='selectionInfo' label={t('member:selection.description')} />
                    </Grid>
                  </GridWrapper>
                ) : (
                  <LoadingOverlay loading={loading} />
                )}
              </>
            }
            dialogActions={
              <Grid container justifyContent='space-between'>
                <Grid item>
                  <Button
                    size='large'
                    onClick={() => setOpenDeleteDialog(true)}
                    disableElevation
                    variant='outlined'
                    color='error'
                    type='button'
                    fullWidth
                  >
                    {t('common:button.delete')}
                  </Button>
                </Grid>
                <Grid item>
                  <Button form='update-selection-form' size='large' disableElevation variant='contained' color='primary' type='submit' fullWidth>
                    {t('common:button.save')}
                  </Button>
                </Grid>
              </Grid>
            }
          />
        </form>
      </FormProvider>
      <DeleteConfirmationDialog
        title={t('common:confirmDelete')}
        value=''
        modaltext={t('common:irreversibleAction')}
        onClose={(value) => {
          if (value) {
            onHandleDelete();
          } else {
            setOpenDeleteDialog(false);
          }
        }}
        open={openDeleteDialog}
      />
    </>
  );
};
